import { useMutation, useQuery } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ClientFeeOption, ClientFeeTierInput, ClientFeeTierRow, ClientShareClassInput, ClientShareClassRow } from "_graphql-types";
import { useEffect, useState } from "react";
import { useNotify } from "react-admin";
import { CLIENT_FEE_OPTIONS, DELETE_CLIENT_SHARE_CLASS, SAVE_CLIENT_SHARE_CLASS } from "./graphql";
import { parseNumber, toText } from "./logic";
import {
  AllClientsToggle,
  ClientPicker,
  confirmDiscard,
  EmptyState,
  FilterBar,
  FundPicker,
  Page,
  QueryState,
  useSelectedClient,
  useSelectedFund,
  useSelectedShareClass,
  useShareClasses,
} from "./shared";

/** FeeUnitOptionEnum: a percentage of the balance (else dollars). */
const UNIT_PERCENTAGE = 1;

interface TierDraft {
  threshold: string;
  rate: string;
  isFlat: boolean;
}

interface ShareClassDraft {
  shareClassId: number | null;
  name: string;
  unfunded: boolean;
  feeAumOptionId: number;
  feeRateOptionId: number;
  feeUnitOptionId: number;
  mgmtTiers: TierDraft[];
  adminTiers: TierDraft[];
}

const tierDraft = (t: ClientFeeTierRow): TierDraft => ({ threshold: toText(t.threshold), rate: toText(t.rate), isFlat: t.isFlat });

function draftOf(sc: ClientShareClassRow): ShareClassDraft {
  return {
    shareClassId: sc.shareClassId,
    name: sc.name,
    unfunded: !!sc.unfunded,
    feeAumOptionId: sc.feeAumOptionId,
    feeRateOptionId: sc.feeRateOptionId,
    feeUnitOptionId: sc.feeUnitOptionId,
    mgmtTiers: sc.mgmtTiers.map(tierDraft),
    adminTiers: sc.adminTiers.map(tierDraft),
  };
}

function newDraft(aum: ClientFeeOption[]): ShareClassDraft {
  if (!aum.length) throw new Error("No fee AUM options were loaded");
  return {
    shareClassId: null,
    name: "",
    unfunded: false,
    feeAumOptionId: aum[0].id,
    feeRateOptionId: UNIT_PERCENTAGE,
    feeUnitOptionId: UNIT_PERCENTAGE,
    mgmtTiers: [],
    adminTiers: [],
  };
}

function tierInputs(tiers: TierDraft[], label: string): ClientFeeTierInput[] {
  return tiers.map((t, i) => {
    const threshold = parseNumber(t.threshold, `${label} tier ${i + 1} threshold`);
    const rate = parseNumber(t.rate, `${label} tier ${i + 1} rate`);
    if (threshold === null || rate === null) throw new Error(`${label} tier ${i + 1} needs a threshold and a rate`);
    return { threshold, rate, isFlat: t.isFlat };
  });
}

function shareClassInput(draft: ShareClassDraft, rcgFundId: number, clientId: number): ClientShareClassInput {
  if (draft.name.trim() === "") throw new Error("The share class name is required");
  return {
    shareClassId: draft.shareClassId,
    rcgFundId,
    clientId,
    name: draft.name.trim(),
    unfunded: draft.unfunded,
    feeAumOptionId: draft.feeAumOptionId,
    feeRateOptionId: draft.feeRateOptionId,
    feeUnitOptionId: draft.feeUnitOptionId,
    mgmtTiers: tierInputs(draft.mgmtTiers, "Management"),
    adminTiers: tierInputs(draft.adminTiers, "Administration"),
  };
}

function TierEditor({
  label,
  tiers,
  percent,
  onChange,
}: {
  label: string;
  tiers: TierDraft[];
  percent: boolean;
  onChange: (tiers: TierDraft[]) => void;
}) {
  const set = (i: number, patch: Partial<TierDraft>) => onChange(tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2">{label} fee tiers</Typography>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {tiers.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No tiers: no {label.toLowerCase()} fee.
          </Typography>
        )}
        {tiers.map((t, i) => (
          <Stack direction="row" spacing={1} alignItems="center" key={i}>
            <TextField
              size="small"
              sx={{ width: 200 }}
              label="Threshold"
              value={t.threshold}
              onChange={e => set(i, { threshold: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
            <TextField
              size="small"
              sx={{ width: 160 }}
              label="Annual rate"
              value={t.rate}
              onChange={e => set(i, { rate: e.target.value })}
              InputProps={
                percent
                  ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                  : { startAdornment: <InputAdornment position="start">$</InputAdornment> }
              }
            />
            <FormControlLabel
              control={<Checkbox checked={t.isFlat} onChange={e => set(i, { isFlat: e.target.checked })} />}
              label="Flat"
            />
            <IconButton aria-label="Remove tier" onClick={() => onChange(tiers.filter((_, j) => j !== i))}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          sx={{ alignSelf: "flex-start" }}
          onClick={() => onChange([...tiers, { threshold: "", rate: "", isFlat: false }])}
        >
          Add tier
        </Button>
      </Stack>
    </Paper>
  );
}

const OptionSelect = ({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: ClientFeeOption[];
  value: number;
  onChange: (id: number) => void;
}) => (
  <TextField select size="small" label={label} value={value} onChange={e => onChange(Number(e.target.value))} sx={{ minWidth: 220 }}>
    {options.map(o => (
      <MenuItem key={o.id} value={o.id}>
        {o.label}
      </MenuItem>
    ))}
  </TextField>
);

export function ShareClassEntry() {
  const notify = useNotify();
  const [fundId, setFundId] = useSelectedFund();
  const [clientId, setClientId] = useSelectedClient();
  const [shareClassId, setShareClassId] = useSelectedShareClass();
  const [allClients, setAllClients] = useState(false);
  const { shareClasses, loading, error, refetch } = useShareClasses(fundId, clientId);
  const options = useQuery(CLIENT_FEE_OPTIONS);
  const aum = options.data?.clientFeeOptions.aum ?? [];
  const rate = options.data?.clientFeeOptions.rate ?? [];
  const unit = options.data?.clientFeeOptions.unit ?? [];
  const [draft, setDraft] = useState<ShareClassDraft | null>(null);
  const [save, saving] = useMutation(SAVE_CLIENT_SHARE_CLASS);
  const [remove, removing] = useMutation(DELETE_CLIENT_SHARE_CLASS);

  const selected = shareClasses.find(s => s.shareClassId === shareClassId) ?? null;
  // Load the selected share class into the form; keep a new one being typed.
  useEffect(() => {
    if (selected) setDraft(draftOf(selected));
    else setDraft(d => (d?.shareClassId === null ? d : null));
  }, [selected]);

  const patch = (p: Partial<ShareClassDraft>) => setDraft(d => (d ? { ...d, ...p } : d));
  // A new share class counts as unsaved from the start; an existing one once it differs.
  const dirty = draft !== null && (selected === null || JSON.stringify(draft) !== JSON.stringify(draftOf(selected)));
  const leave = () => confirmDiscard(dirty ? 1 : 0);

  const onSave = async () => {
    if (!draft || fundId === null || clientId === null) return;
    try {
      const { data } = await save({ variables: { input: shareClassInput(draft, fundId, clientId) } });
      if (!data) throw new Error("The save returned nothing");
      await refetch();
      setShareClassId(data.saveClientShareClass);
      notify("Share class saved", { type: "success" });
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), { type: "error" });
    }
  };

  const onDelete = async () => {
    if (!selected || !window.confirm(`Delete share class ${selected.name}?`)) return;
    try {
      await remove({ variables: { shareClassId: selected.shareClassId } });
      setShareClassId(null);
      setDraft(null);
      await refetch();
      notify("Share class deleted", { type: "success" });
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), { type: "error" });
    }
  };

  return (
    <Page title="Share Class Entry" description="A client's share classes in a fund, with their fee schedules.">
      <FilterBar>
        <FundPicker
          value={fundId}
          onChange={id => {
            if (!leave()) return;
            setFundId(id);
            setShareClassId(null);
          }}
        />
        <ClientPicker
          rcgFundId={fundId}
          allClients={allClients}
          value={clientId}
          onChange={id => {
            if (!leave()) return;
            setClientId(id);
            setShareClassId(null);
          }}
        />
        <AllClientsToggle value={allClients} onChange={setAllClients} />
      </FilterBar>
      <QueryState loading={loading || options.loading} error={error ?? options.error} />
      {fundId === null || clientId === null ? (
        <EmptyState>Pick a fund and a client to see their share classes.</EmptyState>
      ) : (
        <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
          <Paper variant="outlined" sx={{ width: 300, flexShrink: 0 }}>
            <List dense>
              {shareClasses.map(s => (
                <ListItemButton
                  key={s.shareClassId}
                  selected={s.shareClassId === shareClassId}
                  onClick={() => s.shareClassId !== shareClassId && leave() && setShareClassId(s.shareClassId)}
                >
                  <ListItemText primary={s.name} secondary={s.derived ? "Derived" : undefined} />
                </ListItemButton>
              ))}
              {!loading && shareClasses.length === 0 && (
                <ListItemText sx={{ px: 2 }} secondary="No share classes for this client in this fund yet" />
              )}
            </List>
            <Button
              startIcon={<AddIcon />}
              sx={{ m: 1 }}
              disabled={!aum.length}
              onClick={() => {
                if (!leave()) return;
                setShareClassId(null);
                setDraft(newDraft(aum));
              }}
            >
              New share class
            </Button>
          </Paper>
          {draft ? (
            <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 360, maxWidth: 900 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {selected ? selected.name : "New share class"}
                </Typography>
                {selected?.derived && (
                  <Alert severity="info">This share class is derived: its flows, balances and fees come from other share classes.</Alert>
                )}
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                  <TextField size="small" sx={{ width: 320 }} label="Name" required value={draft.name} onChange={e => patch({ name: e.target.value })} />
                  <FormControlLabel
                    control={<Checkbox checked={draft.unfunded} onChange={e => patch({ unfunded: e.target.checked })} />}
                    label="Unfunded"
                  />
                </Stack>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <OptionSelect label="AUM option" options={aum} value={draft.feeAumOptionId} onChange={id => patch({ feeAumOptionId: id })} />
                  <OptionSelect
                    label="Unit option"
                    options={unit}
                    value={draft.feeUnitOptionId}
                    // The legacy defaults: unit 1 -> rate option 1, unit 2 -> rate option 2.
                    onChange={id => patch(id === 1 || id === 2 ? { feeUnitOptionId: id, feeRateOptionId: id } : { feeUnitOptionId: id })}
                  />
                  <OptionSelect label="Rate option" options={rate} value={draft.feeRateOptionId} onChange={id => patch({ feeRateOptionId: id })} />
                </Stack>
                <TierEditor
                  label="Management"
                  tiers={draft.mgmtTiers}
                  percent={draft.feeUnitOptionId === UNIT_PERCENTAGE}
                  onChange={mgmtTiers => patch({ mgmtTiers })}
                />
                <TierEditor
                  label="Administration"
                  tiers={draft.adminTiers}
                  percent={draft.feeUnitOptionId === UNIT_PERCENTAGE}
                  onChange={adminTiers => patch({ adminTiers })}
                />
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button variant="contained" onClick={onSave} disabled={saving.loading || !dirty}>
                    {saving.loading ? "Saving..." : "Save"}
                  </Button>
                  <Button disabled={!dirty} onClick={() => leave() && setDraft(selected ? draftOf(selected) : null)}>
                    {selected ? "Discard changes" : "Cancel"}
                  </Button>
                  <Box sx={{ flex: 1 }} />
                  {selected?.deletable && (
                    <Button color="error" variant="outlined" onClick={onDelete} disabled={removing.loading}>
                      Delete
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Paper>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
              Pick a share class to edit it, or add a new one.
            </Typography>
          )}
        </Stack>
      )}
    </Page>
  );
}
